import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
  Button,
  TableHead,
  Paper,
  TableContainer,
  Chip,
  Alert,
} from '@mui/material';
import Icon from 'react-payment-icons';
import { PaymentMethod } from 'renderer/features/settings/schemas/paymentMethods.schemas';
import {
  formatCardExpirationDate,
  formatQueryError,
} from 'renderer/shared/formatters/formatters';
import { useEditPaymentMethodMutation } from 'renderer/features/settings/services/paymentMethods.endpoints';

const tableStyles = {
  tableContainer: {
    // marginTop: '20px',
    marginBottom: '20px',
    border: '1px solid rgba(224, 224, 224, 1)',
  },
  table: {
    minWidth: 650,
  },
  tableHead: {
    backgroundColor: '#f5f5f5',
  },
  tableCell: {
    borderBottom: '1px solid rgba(224, 224, 224, 1)',
  },
  tableRow: {
    '&:last-child td': {
      border: 0,
    },
  },
};

interface PaymentMethodsTableProps {
  paymentMethods: PaymentMethod[];
  onDeleteClick: (card: PaymentMethod) => void;
  onEditClick: (card: PaymentMethod) => void;
  userId: string;
  // onMakeDefaultClick: (card: PaymentMethod) => void;
}

const PaymentMethodsTable: React.FC<PaymentMethodsTableProps> = ({
  paymentMethods,
  onDeleteClick,
  onEditClick,
  userId,
  // onMakeDefaultClick,
}) => {
  const [updateCard, { isLoading, error: editError }] =
    useEditPaymentMethodMutation();
  return (
    <>
      {editError && (
        <Alert severity="error">{formatQueryError(editError)}</Alert>
      )}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead sx={tableStyles.tableHead}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>CARD TYPE</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                EXPIRES
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                ACTIONS
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paymentMethods.map((method) => (
              <TableRow
                key={method._id}
                sx={{
                  '&:last-child td': {
                    border: 0,
                  },
                }}
              >
                <TableCell align="center">
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Icon
                      id={method.cardType.toLowerCase()}
                      style={{ marginRight: 8, width: 45, height: 45 }}
                    />
                    <span>
                      {method.cardType} ****{method.last4}
                    </span>
                    {method.default && (
                      <Chip
                        label="Default"
                        size="small"
                        color="primary"
                        style={{
                          marginLeft: 8,
                          color: 'white',
                          borderRadius: 12,
                        }}
                      />
                    )}
                  </div>
                </TableCell>

                <TableCell align="center">
                  Expires{' '}
                  {formatCardExpirationDate(
                    method.expirationMonth ?? '',
                    method.expirationYear ?? '',
                  )}
                </TableCell>
                <TableCell align="center">
                  <Button
                    size="small"
                    sx={{ mr: 2 }}
                    variant="outlined"
                    disabled={!!method.default || isLoading}
                    onClick={() =>
                      updateCard({
                        id: method._id,
                        payload: { userId, default: true },
                      })
                    }
                  >
                    Make Default
                  </Button>
                  <Button
                    size="small"
                    sx={{ mr: 2 }}
                    variant="outlined"
                    onClick={() => onEditClick(method)}
                  >
                    Edit
                  </Button>
                  <Button
                    size="small"
                    sx={{ mr: 2 }}
                    variant="outlined"
                    color="error"
                    onClick={() => onDeleteClick(method)}
                  >
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default PaymentMethodsTable;
